import { Eye, EyeOff } from 'lucide-react';
import { useState, useRef } from 'react';
import Link from 'next/link';
import type { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { getCsrfToken, signIn } from 'next-auth/react';
import type { Provider } from 'next-auth/providers/index';
import { useForm, Controller } from 'react-hook-form';
import { useRouter } from 'next/router';
import '../../../styles/carketa.css';
import themeConfig from '../../configs/themeConfig';
import BlankLayout from '../../@core/layouts/BlankLayout';
import FooterIllustrationsV2 from '../../views/pages/auth/FooterIllustrationsV2';
import { useApp } from 'src/@carketa/app/AppContext';
import { CarketaIcon } from 'src/@carketa/components/icons/CarketaIcon';
import { hasVelocifi } from 'src/utils/velocifi/velocifi';
import { VelocifiImg } from 'src/@carketa/velocifi/VelocifyImg';
import { Alert, AlertDescription } from 'src/ui/alert';
import { Button } from 'src/ui/button';
import { Input } from 'src/ui/input';
import { FieldLabel } from 'src/ui/field-label';
import { Typography } from 'src/ui/typography';
import { cn } from 'src/utils/@/lib/utils';

const defaultValues = {
  password: '',
  username: '',
};

interface FormData {
  username: string;
  password: string;
}

type LoginPageProps = {
  csrfToken: string;
  providers: Array<Provider>;
};

const LoginPage: NextPage<LoginPageProps> = ({ csrfToken }) => {
  const has_velocifi = hasVelocifi();

  const router = useRouter();
  const { callbackUrl, error } = router.query;
  const { isFlutter } = useApp();
  const [showPassword, setShowPassword] = useState(false);
  const form = useRef<HTMLFormElement>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues,
    mode: 'onBlur',
  });

  const onSubmit = (data: FormData) => {
    signIn('userNameAndPassword', {
      username: data.username,
      password: data.password,
    });
  };

  return (
    <BlankLayout>
      <div className="content-right">
        <div className="relative hidden flex-1 items-center justify-center md:flex">
          <div className="p-20 lg:p-40 lg:pr-0">
            <img
              alt="login-illustration"
              src="/images/pages/sign-in-visual.svg"
              className="max-w-[35rem] lg:max-w-[48rem]"
            />
          </div>
          <FooterIllustrationsV2 />
        </div>
        <div className="flex min-h-screen w-full bg-white md:max-w-[450px]">
          <div className="flex h-full w-full items-center justify-center p-12">
            <div className="w-full max-w-[400px] md:max-w-none">
              <div className="absolute left-10 top-[30px] flex items-center justify-center">
                <CarketaIcon width={50} height={50} fill="black" />
                {has_velocifi ? (
                  <div className="pt-3">
                    <VelocifiImg width={200} height={90} />
                  </div>
                ) : (
                  <Typography variant="h6" style="bold" className="ml-2 text-2xl uppercase leading-none">
                    {themeConfig.templateName}
                  </Typography>
                )}
              </div>
              {isFlutter && <div className="h-[50px]" />}
              <div className="mb-6">
                <Typography variant="h5" style="bold" className="mb-3 max-md:mt-16">
                  Welcome to {has_velocifi ? 'VelociFI' : themeConfig.templateName}! 👋🏻
                </Typography>
                <p id="loginAdvise" className="text-sm font-sans leading-normal">
                  Please sign in to your account and start the adventure
                </p>
              </div>
              {error && (
                <div className="mb-6">
                  <Alert id="loginError" severity="error">
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </div>
              )}
              <form
                ref={form}
                noValidate
                autoComplete="off"
                method="post"
                action={`/api/auth/callback/credentials${callbackUrl ? `?callbackUrl=${callbackUrl.toString()}` : ''}`}
                onSubmit={handleSubmit(onSubmit)}
              >
                <input name="csrfToken" type="hidden" defaultValue={csrfToken} />
                <div className="mb-6">
                  <FieldLabel htmlFor="auth-login-email">Email</FieldLabel>
                  <Controller
                    name="username"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <Input
                        id="auth-login-email"
                        name="username"
                        autoFocus
                        placeholder="you@dealership.com"
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                        className={cn(errors.username && 'border-destructive', 'h-12')}
                      />
                    )}
                  />
                  {errors.username && <p className="mt-1 text-xs text-destructive">{errors.username.message}</p>}
                </div>
                <div>
                  <FieldLabel htmlFor="auth-login-v2-password">Password</FieldLabel>
                  <Controller
                    name="password"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { value, onChange, onBlur } }) => (
                      <Input
                        value={value}
                        onBlur={onBlur}
                        onChange={onChange}
                        id="auth-login-v2-password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        className={cn(errors.password && 'border-destructive', 'h-12')}
                        suffix={
                          <button
                            type="button"
                            className="pointer-events-auto"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                          </button>
                        }
                      />
                    )}
                  />
                  {errors.password && (
                    <p className="mt-1 text-xs text-destructive" id="">
                      {errors.password.message}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center justify-between py-8">
                  <Link href="/magiclink" id="forgotPassword" className="text-sm text-primary no-underline">
                    Vendor Login?
                  </Link>
                  <Link href="/forgot-password" id="forgotPassword" className="text-sm text-primary no-underline">
                    Forgot Password?
                  </Link>
                </div>
                <Button fullWidth size="lg" type="submit" variant="contained" color="primary" className="mb-14">
                  Login
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </BlankLayout>
  );
};

export default LoginPage;

export const getServerSideProps: GetServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const error = ctx.query.error || null;
  if (error?.toString()?.includes('Amplifi')) {
    return {
      redirect: {
        destination: `/amplifi/login?error=${error.toString()}`,
        permanent: false,
      },
    };
  }

  const csrfToken = await getCsrfToken(ctx);
  return {
    props: {
      csrfToken: csrfToken || null,
    },
  };
};
